/* global process */

/**
 * Send org text to a reMarkable tablet through a remarkable-mcp server.
 *
 * organice is a static browser app, so it talks to the MCP server itself:
 * Streamable HTTP (JSON-RPC over fetch) to the server's endpoint, with an
 * access token from the server's OAuth authorization server. On fireweaver.org
 * that is the shared gateway at rm-mcp.fireweaver.org, which checks tokens of
 * the Keycloak realm `mcp` (mcp-platform ADR 0005) and passes the add-only
 * write tool `remarkable_markdown_to_pdf` (rm-mcp `allow_tools`).
 *
 * Sign-in is an authorization-code + PKCE flow in a popup, so whatever is open
 * in organice (including unsaved edits) stays as it is. It deliberately does
 * not use @bity/oauth2-auth-code-pkce: that library keeps its state under one
 * fixed localStorage key, which the GitLab sync back end already owns.
 *
 * Configuration, all build-time (Parcel inlines process.env):
 *   REACT_APP_REMARKABLE_MCP_URL       e.g. https://rm-mcp.fireweaver.org/mcp
 *   REACT_APP_REMARKABLE_OIDC_ISSUER   e.g. https://id.fireweaver.org/realms/mcp
 *   REACT_APP_REMARKABLE_OIDC_CLIENT_ID  a public client with PKCE (S256)
 *   REACT_APP_REMARKABLE_OIDC_SCOPE    the server's audience scope, e.g. rm-mcp
 *   REACT_APP_REMARKABLE_FOLDER        destination folder (default "/"); it must exist
 * The feature is hidden unless the first three are set.
 */

export const REMARKABLE_CALLBACK_PATH = '/remarkable/callback';

const TOKENS_KEY = 'remarkableOidcTokens';
const PENDING_KEY = 'remarkableOidcPending';
const SIGN_IN_TIMEOUT_MS = 5 * 60 * 1000;
const TOKEN_EXPIRY_MARGIN_MS = 30 * 1000;
const MCP_PROTOCOL_VERSION = '2025-06-18';

export const remarkableConfig = () => ({
  mcpUrl: process.env.REACT_APP_REMARKABLE_MCP_URL,
  issuer: (process.env.REACT_APP_REMARKABLE_OIDC_ISSUER || '').replace(/\/$/, ''),
  clientId: process.env.REACT_APP_REMARKABLE_OIDC_CLIENT_ID,
  scope: process.env.REACT_APP_REMARKABLE_OIDC_SCOPE || '',
  folder: process.env.REACT_APP_REMARKABLE_FOLDER || '/',
});

export const isRemarkableConfigured = (config = remarkableConfig()) =>
  !!(config.mcpUrl && config.issuer && config.clientId);

const redirectUri = () => window.location.origin + REMARKABLE_CALLBACK_PATH;

export class RemarkableError extends Error {
  constructor(message, { kind = 'error', suggestion } = {}) {
    super(message);
    this.name = 'RemarkableError';
    this.kind = kind;
    this.suggestion = suggestion;
  }
}

// --- storage --------------------------------------------------------------

const readJson = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || 'null');
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  if (value === null) {
    window.localStorage.removeItem(key);
  } else {
    window.localStorage.setItem(key, JSON.stringify(value));
  }
};

const storeTokenResponse = (response, previous = {}) => {
  const tokens = {
    accessToken: response.access_token,
    // Keycloak rotates refresh tokens (ADR 0009); keep the old one only if
    // the server sent none.
    refreshToken: response.refresh_token || previous.refreshToken || null,
    expiresAt: Date.now() + (Number(response.expires_in) || 60) * 1000,
  };
  writeJson(TOKENS_KEY, tokens);
  return tokens;
};

export const forgetRemarkableSignIn = () => writeJson(TOKENS_KEY, null);

/** True when a send can probably go ahead without opening the sign-in popup. */
export const hasRemarkableSession = () => {
  const tokens = readJson(TOKENS_KEY);
  return !!tokens && (!!tokens.refreshToken || tokens.expiresAt > Date.now());
};

// --- PKCE -----------------------------------------------------------------

const base64Url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const randomString = (byteCount = 32) =>
  base64Url(window.crypto.getRandomValues(new Uint8Array(byteCount)));

const codeChallenge = async (verifier) =>
  base64Url(await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));

const tokenRequest = async (config, params) => {
  let response;
  try {
    response = await fetch(`${config.issuer}/protocol/openid-connect/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ client_id: config.clientId, ...params }).toString(),
    });
  } catch (e) {
    throw new RemarkableError('Could not reach the reMarkable sign-in server.', {
      kind: 'network',
    });
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new RemarkableError(body.error_description || body.error || 'Sign-in was refused.', {
      kind: 'auth',
    });
  }
  return body;
};

/**
 * Open the sign-in popup. Call it synchronously from a click handler, before
 * any `await`, or the browser blocks the popup.
 *
 * @returns {Promise<void>} resolves once the callback page has stored tokens
 */
export const signInToRemarkable = (config = remarkableConfig()) => {
  // Opened first, navigated once the challenge is computed (that is async).
  const popup = window.open('', 'remarkable-sign-in', 'width=520,height=680');
  if (!popup) {
    return Promise.reject(
      new RemarkableError('The browser blocked the sign-in window. Allow popups and try again.', {
        kind: 'popup-blocked',
      })
    );
  }

  const verifier = randomString(48);
  const state = randomString(16);
  writeJson(PENDING_KEY, { verifier, state, startedAt: Date.now() });
  writeJson(TOKENS_KEY, null);

  return codeChallenge(verifier).then(
    (challenge) =>
      new Promise((resolve, reject) => {
        const params = new URLSearchParams({
          response_type: 'code',
          client_id: config.clientId,
          redirect_uri: redirectUri(),
          scope: ['openid', config.scope].filter(Boolean).join(' '),
          state,
          code_challenge: challenge,
          code_challenge_method: 'S256',
        });
        popup.location.href = `${config.issuer}/protocol/openid-connect/auth?${params}`;

        // The callback runs in the popup and writes the tokens to
        // localStorage. Watch storage rather than postMessage: the identity
        // provider's pages may sever window.opener (COOP).
        const startedAt = Date.now();
        const finish = (error) => {
          window.clearInterval(timer);
          window.removeEventListener('storage', check);
          if (error) reject(error);
          else resolve();
        };
        const check = () => {
          const tokens = readJson(TOKENS_KEY);
          const pending = readJson(PENDING_KEY);
          if (tokens && tokens.accessToken) return finish();
          if (pending && pending.error) {
            writeJson(PENDING_KEY, null);
            return finish(new RemarkableError(pending.error, { kind: 'auth' }));
          }
          if (Date.now() - startedAt > SIGN_IN_TIMEOUT_MS) {
            return finish(new RemarkableError('Sign-in timed out.', { kind: 'auth' }));
          }
          if (popup.closed && !pending) {
            return finish(new RemarkableError('Sign-in failed.', { kind: 'auth' }));
          }
          if (popup.closed && Date.now() - startedAt > 1000) {
            // Closed by the user before finishing; give a late storage write a moment.
            window.setTimeout(() => {
              const late = readJson(TOKENS_KEY);
              if (late && late.accessToken) finish();
              else finish(new RemarkableError('Sign-in was cancelled.', { kind: 'cancelled' }));
            }, 500);
            window.clearInterval(timer);
          }
        };
        const timer = window.setInterval(check, 500);
        window.addEventListener('storage', check);
      })
  );
};

export const isRemarkableCallback = () => window.location.pathname === REMARKABLE_CALLBACK_PATH;

/**
 * Runs in the popup on REMARKABLE_CALLBACK_PATH: redeem the code, store the
 * tokens for the opener, close.
 *
 * @returns {Promise<string>} a line to show if the window cannot close itself
 */
export const completeRemarkableSignIn = async (config = remarkableConfig()) => {
  const query = new URLSearchParams(window.location.search);
  const pending = readJson(PENDING_KEY);
  const fail = (message) => {
    writeJson(PENDING_KEY, { error: message });
    return message;
  };

  if (query.get('error')) {
    return fail(query.get('error_description') || query.get('error'));
  }
  if (!pending || !pending.verifier || pending.state !== query.get('state')) {
    return fail('This sign-in link is stale. Start again from organice.');
  }
  try {
    const response = await tokenRequest(config, {
      grant_type: 'authorization_code',
      code: query.get('code'),
      redirect_uri: redirectUri(),
      code_verifier: pending.verifier,
    });
    storeTokenResponse(response);
    writeJson(PENDING_KEY, null);
  } catch (e) {
    return fail(e.message);
  }
  window.close();
  return 'Signed in to reMarkable. You can close this window.';
};

/**
 * A valid access token, refreshed if needed. Never opens a popup.
 * @throws {RemarkableError} kind 'auth' when the user must sign in again
 */
export const getRemarkableAccessToken = async (config = remarkableConfig()) => {
  const tokens = readJson(TOKENS_KEY);
  if (!tokens) throw new RemarkableError('Not signed in to reMarkable.', { kind: 'auth' });
  if (tokens.expiresAt - TOKEN_EXPIRY_MARGIN_MS > Date.now()) return tokens.accessToken;
  if (!tokens.refreshToken) {
    forgetRemarkableSignIn();
    throw new RemarkableError('The reMarkable sign-in expired.', { kind: 'auth' });
  }
  try {
    const response = await tokenRequest(config, {
      grant_type: 'refresh_token',
      refresh_token: tokens.refreshToken,
    });
    return storeTokenResponse(response, tokens).accessToken;
  } catch (e) {
    if (e.kind === 'auth') forgetRemarkableSignIn();
    throw e;
  }
};

// --- MCP over Streamable HTTP --------------------------------------------

/** JSON-RPC messages from a JSON or text/event-stream response body. */
export const parseMcpMessages = (contentType, text) => {
  if (!text) return [];
  if ((contentType || '').includes('text/event-stream')) {
    return text
      .split(/\r?\n\r?\n/)
      .map((event) =>
        event
          .split(/\r?\n/)
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).replace(/^ /, ''))
          .join('\n')
      )
      .filter(Boolean)
      .map((data) => {
        try {
          return JSON.parse(data);
        } catch (e) {
          return null;
        }
      })
      .filter(Boolean);
  }
  const parsed = JSON.parse(text);
  return Array.isArray(parsed) ? parsed : [parsed];
};

export class McpSession {
  constructor(url, accessToken) {
    this.url = url;
    this.accessToken = accessToken;
    this.sessionId = null;
    this.protocolVersion = null;
    this.nextId = 1;
  }

  headers() {
    const headers = {
      Authorization: `Bearer ${this.accessToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/json, text/event-stream',
    };
    if (this.sessionId) headers['Mcp-Session-Id'] = this.sessionId;
    if (this.protocolVersion) headers['MCP-Protocol-Version'] = this.protocolVersion;
    return headers;
  }

  async post(message) {
    let response;
    try {
      response = await fetch(this.url, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify(message),
      });
    } catch (e) {
      // A CORS refusal looks exactly like this, too.
      throw new RemarkableError('Could not reach the reMarkable server.', { kind: 'network' });
    }
    if (response.status === 401) {
      throw new RemarkableError('The reMarkable server did not accept the sign-in.', {
        kind: 'auth',
      });
    }
    if (response.status === 403) {
      throw new RemarkableError('This account has no access to the reMarkable server.', {
        kind: 'forbidden',
      });
    }
    if (response.status === 429) {
      throw new RemarkableError('The reMarkable server is rate-limiting. Try again shortly.', {
        kind: 'rate-limited',
      });
    }
    if (!response.ok && response.status !== 202) {
      throw new RemarkableError(`The reMarkable server answered HTTP ${response.status}.`, {
        kind: 'http',
      });
    }
    const sessionId = response.headers.get('Mcp-Session-Id');
    if (sessionId) this.sessionId = sessionId;
    const text = response.status === 202 ? '' : await response.text();
    return parseMcpMessages(response.headers.get('Content-Type'), text);
  }

  async request(method, params) {
    const id = this.nextId++;
    const messages = await this.post({ jsonrpc: '2.0', id, method, params });
    const reply = messages.find((message) => message.id === id);
    if (!reply) throw new RemarkableError(`No answer to ${method}.`, { kind: 'protocol' });
    if (reply.error) {
      throw new RemarkableError(reply.error.message || `${method} failed.`, { kind: 'protocol' });
    }
    return reply.result;
  }

  notify(method, params) {
    return this.post({ jsonrpc: '2.0', method, ...(params ? { params } : {}) });
  }

  async initialize() {
    const result = await this.request('initialize', {
      protocolVersion: MCP_PROTOCOL_VERSION,
      capabilities: {},
      clientInfo: { name: 'organice', version: '1' },
    });
    this.protocolVersion = result.protocolVersion || MCP_PROTOCOL_VERSION;
    await this.notify('notifications/initialized');
    return result;
  }

  async close() {
    if (!this.sessionId) return;
    try {
      await fetch(this.url, { method: 'DELETE', headers: this.headers() });
    } catch (e) {
      // The server expires idle sessions on its own.
    }
  }

  callTool(name, args) {
    return this.request('tools/call', { name, arguments: args });
  }
}

/**
 * Turn a tools/call result into the payload, or throw the tool's error.
 * remarkable-mcp reports failures as `{"_error": {message, suggestion}}` text,
 * not always with `isError` set.
 */
export const unwrapToolResult = (result) => {
  const text = ((result && result.content) || [])
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('\n');
  let payload = null;
  try {
    payload = JSON.parse(text);
  } catch (e) {
    payload = null;
  }
  if (payload && payload._error) {
    throw new RemarkableError(payload._error.message || 'The upload failed.', {
      kind: 'tool',
      suggestion: payload._error.suggestion,
    });
  }
  if (result && result.isError) {
    throw new RemarkableError(text || 'The upload failed.', { kind: 'tool' });
  }
  return payload || text;
};

/**
 * Render Markdown to a PDF on the server and upload it to the tablet.
 * Assumes a sign-in exists (see hasRemarkableSession / signInToRemarkable).
 */
export const sendMarkdownToRemarkable = async (
  { markdown, documentName, folder },
  config = remarkableConfig()
) => {
  const accessToken = await getRemarkableAccessToken(config);
  const session = new McpSession(config.mcpUrl, accessToken);
  try {
    await session.initialize();
    const result = await session.callTool('remarkable_markdown_to_pdf', {
      markdown,
      document_name: documentName,
      parent_folder: folder || config.folder,
    });
    return unwrapToolResult(result);
  } catch (e) {
    if (e.kind === 'auth') forgetRemarkableSignIn();
    throw e;
  } finally {
    session.close();
  }
};
